import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckboxButton, CheckboxButtonGroup, SectionHeading, TableCustom, WrapperAdvancedSearch } from '../../components/common';
import { useManagedContext } from '../../components/hooks';
import { useLista } from '../../components/hooks/useLista';
import { useUsuarios } from '../../components/hooks/useUsuarios';
import { APIS } from '../../config/apis';
import { ALERT_TYPE, AvanceProceso, formatAvanceProceso, PROCESO_STATE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { PERMISSIONS } from '../../consts/permissions';
import { getDateToString } from '../../utils/convert';
import { standardRequest } from '../../utils/requests';
import { usePermissions } from '../../components/hooks/usePermissions';
import ProcesoModal from '../../components/controls/ProcesoModal';
import { ServerRequest } from '../../utils/apiweb';

const USUARIO_CACHE_KEY = 'ControlProcesos_UsuarioCache';

const ControlProcesosView = () => {
    const { setIsLoading } = useManagedContext();

    const { hasPermission } = usePermissions();
    const canEditProceso = hasPermission(PERMISSIONS.PROCESO_EDIT);

    const [list, setList] = useState([]);
    const [idUsuarioFiltro, setIdUsuarioFiltro] = useState(0);
    const [tableVisible, setTableVisible] = useState(false);
    const [procesoModal, setProcesoModal] = useState({ show: false, proceso: null });

    const searchRef = useRef();
    const [autoRefresh, setAutoRefresh] = useState(true);
    const autoRefreshRef = useRef(autoRefresh);

    const [getNombreUsuario, resolveUsuarios] = useUsuarios({
        key: USUARIO_CACHE_KEY,
    });

    const usuarioCache = useSelector((state) => state.memo.cache[USUARIO_CACHE_KEY]?.value);

    const usuarioOptions = useMemo(() => {
        if (!usuarioCache) return [];
        return Object.entries(usuarioCache)
            .filter(([, nombre]) => nombre)
            .map(([id, nombre]) => ({ id: Number(id), nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [usuarioCache]);

    const filteredList = useMemo(() => {
        if (idUsuarioFiltro === 0) return list;
        return list.filter((row) => row.idUsuarioCreacion === idUsuarioFiltro);
    }, [list, idUsuarioFiltro]);

    const [, getRowLista] = useLista({
        listas: ['EstadoProceso'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'EstadoProceso',
            timeout: 0,
        },
    });

    useEffect(() => {
        autoRefreshRef.current = autoRefresh;
    }, [autoRefresh]);

    useEffect(() => {
        SearchProcesos();
        const interval = setInterval(() => {
            if (autoRefreshRef.current) {
                SearchProcesos();
            }
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    const getDescEstadoProceso = (proceso) => {
        const abortando = [PROCESO_STATE.PENDIENTE, PROCESO_STATE.PROGRESO].includes(proceso.idEstadoProceso) && proceso.cancelacionSolicitada;
        const row = getRowLista('EstadoProceso', proceso.idEstadoProceso);
        return (row && abortando && proceso.cancelable) ? `${row.nombre} (cancelando...)` :
               (row && abortando && proceso.pausable) ? `${row.nombre} (pausando...)` :
               (row) ? row.nombre : '';
    };

    const tableColumns = [
        { Header: 'Descripción', accessor: 'descripcion', width: '22%', disableSortBy: true },
        {
            Header: 'Estado',
            Cell: (props) => getDescEstadoProceso(props.row.original),
            accessor: 'idEstadoProceso',
            width: '10%',
            disableSortBy: true,
        },
        {
            Header: 'Avance',
            Cell: (props) => <AvanceProceso avance={props.row.original.avance} idEstadoProceso={props.row.original.idEstadoProceso} />,
            csvValue: (row) => formatAvanceProceso(row.avance),
            accessor: 'avance',
            width: '12%',
            ellipsis: false,
        },
        {
            Header: 'Prog.',
            Cell: (props) => getDateToString(props.value, true),
            accessor: 'fechaProceso',
            width: '14%',
            ellipsis: false,
        },
        {
            Header: 'Inicio',
            Cell: (props) => getDateToString(props.value, true),
            accessor: 'fechaInicio',
            width: '14%',
            ellipsis: false,
        },
        {
            Header: 'Fin',
            Cell: (props) => getDateToString(props.value, true),
            accessor: 'fechaFin',
            width: '14%',
            ellipsis: false,
        },
        {
            Header: 'Usuario',
            Cell: (props) => getNombreUsuario(props.row.original.idUsuarioCreacion),
            accessor: 'idUsuarioCreacion',
            width: '12%',
            disableSortBy: true,
        },
        {
            Header: '',
            Cell: (data) => (
                <div className="action">
                    <div
                        onClick={() => {
                            setProcesoModal({ show: true, proceso: data.row.original });
                        }}
                        className="link"
                    >
                        <span className="material-symbols-outlined" title="Ver">search</span>
                    </div>
                </div>
            ),
            id: 'abm',
            accessor: 'identificador',
            width: '4%',
            disableGlobalFilter: true,
            disableSortBy: true,
            ellipsis: false,
        },
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        setTableVisible(true);
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setIsLoading(false);
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setIsLoading(false);
            });
    }
    const callbackError = (error) => {
        setTableVisible(true);
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setIsLoading(false);
    }

    function ModifyCancelProceso(identificador) {

        const callbackSucces = (response) => {
            response.json()
                .then((proceso) => {
                    setIsLoading(false);

                    if (proceso.cancelacionSolicitada && proceso.cancelable)
                        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, `Proceso de cancelación iniciado`);
                    else if (proceso.cancelacionSolicitada && proceso.pausable)
                        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, `Proceso de pausa iniciado`);
                    else
                        ShowToastMessage(ALERT_TYPE.ALERT_WARNING, `Proceso cancelado correctamente`);

                    SearchProcesos();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setIsLoading(false);

                });
        }

        setIsLoading(true);

        const paramsUrl = `/cancel/${identificador}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PROCESO,
            paramsUrl,
            null,
            callbackSucces,
            callbackNoSuccess,
            callbackError
        );

    }

    function SearchProcesos() {

        const callbackSucces = (response) => {
        response.json()
            .then((data) => {
                data.sort((a, b) => b.id - a.id);
                data.forEach((row) => {
                    if (row.fechaCreacion) row.fechaCreacion = new Date(row.fechaCreacion);
                    if (row.fechaProceso) row.fechaProceso = new Date(row.fechaProceso);
                    if (row.fechaInicio) row.fechaInicio = new Date(row.fechaInicio);
                    if (row.fechaFin) row.fechaFin = new Date(row.fechaFin);
                });

                setList(data);
                setTableVisible(true);
                resolveUsuarios(data.map(p => p.idUsuarioCreacion));
            })
            .catch((error) => {
                setTableVisible(true);
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
            });
        }

        const filters = searchRef.current?.filters || {};

        setIdUsuarioFiltro(filters.idUsuarioCreacion ?? 0);

        const formatDate = (date) => {
            if (!date) return '';
            const d = new Date(date);
            return d.toISOString().split('T')[0];
        };

        const paramsUrl = `/filter?` +
            `fechaInicioDesde=${encodeURIComponent(formatDate(filters.fechaInicioDesde))}&` +
            `fechaInicioHasta=${encodeURIComponent(formatDate(filters.fechaInicioHasta))}&` +
            `idTipoProceso=${filters.idTipoProceso}&` +
            `idEstadoProceso=${filters.idEstadoProceso}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PROCESO,
            paramsUrl,
            null,
            callbackSucces,
            callbackNoSuccess,
            callbackError
        );

    };

    const onSearch = () => {
        SearchProcesos();
    };

    return (
        <>
            {procesoModal.show && (
                <ProcesoModal
                    proceso={procesoModal.proceso}
                    disabled={!canEditProceso}
                    showInfo={true}
                    onDismiss={() => setProcesoModal({ show: false, proceso: null })}
                    onConfirm={() => setProcesoModal({ show: false, proceso: null })}
                    onCancel={(row) => {
                        setProcesoModal({ show: false, proceso: null });
                        ModifyCancelProceso(row.identificador);
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: 'Configuración' }, { title: 'Control de procesos' }]}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: 'Tipo de proceso',
                            field: 'idTipoProceso',
                            type: 'entity',
                            entidad: 'TipoProceso',
                            size: 4,
                        },
                        {
                            title: 'Estado de proceso',
                            field: 'idEstadoProceso',
                            type: 'selectLista',
                            lista: 'EstadoProceso',
                            size: 2,
                        },
                        {
                            title: 'Fecha inicio desde',
                            field: 'fechaInicioDesde',
                            type: 'date',
                            size: 2,
                        },
                        {
                            title: 'Fecha inicio hasta',
                            field: 'fechaInicioHasta',
                            type: 'date',
                            size: 2,
                        },
                        {
                            title: 'Usuario',
                            field: 'idUsuarioCreacion',
                            type: 'entity',
                            entidad: 'Usuario',
                            data: usuarioOptions,
                            columns: [
                                { Header: 'Nombre', accessor: 'nombre', width: '95%' },
                            ],
                            size: 2,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        showFilterGlobal={false}
                        className="TableCustomBase m-top-20"
                        columns={tableColumns}
                        data={filteredList}
                        controlSlot={
                            <CheckboxButtonGroup>
                                <CheckboxButton
                                    name="controlProcesos_autoRefresh"
                                    label="Actualización automática"
                                    checked={autoRefresh}
                                    onChange={({ target }) => setAutoRefresh(target.checked)}
                                />
                            </CheckboxButtonGroup>
                        }
                    />
                )}
            </section>
        </>
    );
};

export default ControlProcesosView;
